// src/services/firestoreService.ts
import {
  doc,
  setDoc,
  getDoc,
  collection,
  writeBatch,
  getDocs,
  onSnapshot,
  deleteDoc,
  type Unsubscribe,
  type DocumentData,
  type Firestore,
} from "firebase/firestore";

// ★ 重要：インポートマップのエイリアスに合わせる
import { 
  db, 
  firebaseError, 
  ensureFirestoreOnline, 
  executeWithRetry, 
  getDocumentSafely 
} from "@/firebase";

import type { Wine, ShopProfile, AppData } from "../types";
import { generateStaticWineListHtml, type StaticHtmlData } from '../utils/staticHtmlGenerator';
import {
  wineList as defaultWineList,
  defaultOccasions,
  defaultTastes,
  defaultPrices,
  defaultOrigins,
} from "../data/wineData";

// エラータイプの定義
export interface ServiceResult<T = any> {
  data: T | null;
  error: string | null;
  success: boolean;
}

const createResult = <T>(data: T | null, error: string | null = null): ServiceResult<T> => ({
  data,
  error,
  success: !error
});

const isDbAvailable = (): boolean => {
  if (!db) {
    if (!firebaseError) {
      console.error("Firestore is not initialized. Check Firebase configuration.");
    }
    return false;
  }
  return true;
};

/** 改良された共通エラーハンドリング関数 */
async function withEnhancedRetry<T>(
  operation: () => Promise<T>,
  options: { maxRetries?: number; useFirebaseRetry?: boolean } = {}
): Promise<ServiceResult<T>> {
  if (!db) {
    return createResult(null, "Firestore is not initialized.");
  }

  try {
    let result: T;
    
    if (options.useFirebaseRetry) {
      result = await executeWithRetry(operation, options.maxRetries);
    } else {
      await ensureFirestoreOnline();
      result = await operation();
    }
    
    return createResult(result);
  } catch (error: any) {
    let errorMessage: string;
    
    switch (error.code) {
      case 'unavailable':
        errorMessage = 'ネットワーク接続を確認してください。';
        break;
      case 'permission-denied':
        errorMessage = 'この操作を実行する権限がありません。';
        break;
      case 'unauthenticated':
        errorMessage = '認証が必要です。再ログインしてください。';
        break;
      case 'resource-exhausted':
        errorMessage = 'リクエストの上限に達しました。しばらく時間をおいてから再試行してください。';
        break;
      default:
        errorMessage = error.message || 'エラーが発生しました。';
    }
    
    console.error('Firestore operation failed:', error);
    return createResult(null, errorMessage);
  }
}

/* =========================
 * データ正規化関数
 * ========================= */
const normalizeWineData = (wine: any): Wine => {
  return {
    ...wine,
    // undefinedをnullに変換
    name: wine.name || null,
    origin: wine.origin || null,
    variety: wine.variety || null,
    image: wine.image || null,
    characteristics: Array.isArray(wine.characteristics) ? wine.characteristics : [],
    // 数値フィールドの検証
    price: typeof wine.price === 'number' && wine.price > 0 ? wine.price : null,
    glassPrice: wine.isGlassWine && typeof wine.glassPrice === 'number' && wine.glassPrice > 0 ? wine.glassPrice : null,
    // ブール値の検証
    visible: Boolean(wine.visible),
    isGlassWine: Boolean(wine.isGlassWine),
  };
};

/* =========================
 * 初期データ（新規ユーザー）
 * ========================= */
export const initializeUserData = async (userId: string): Promise<ServiceResult<void>> => {
  if (!isDbAvailable()) {
    return createResult(null, "Firestore is not initialized.");
  }

  return await withEnhancedRetry(async () => {
    const firestore = db as Firestore;
    
    // getDocumentSafelyを使用してユーザードキュメントの存在確認
    const userDocResult = await getDocumentSafely('users', userId);
    
    if (userDocResult.error) {
      throw new Error(userDocResult.error);
    }

    if (!userDocResult.exists) {
      console.log(`Initializing data for new user: ${userId}`);
      const batch = writeBatch(firestore);

      // メインユーザードキュメント
      const userDocRef = doc(firestore, "users", userId);
      batch.set(userDocRef, { createdAt: new Date() });

      // 初期ワイン
      defaultWineList.forEach((wine) => {
        const wineDocRef = doc(firestore, `users/${userId}/wines`, String(wine.id));
        batch.set(wineDocRef, wine);
      });

      // 設定（syncAllDataと同じ構造で保存）
      const settingsRef = doc(firestore, `users/${userId}/settings`, 'appData');
      batch.set(settingsRef, {
        occasions: defaultOccasions,
        tastes: defaultTastes,
        prices: defaultPrices,
        origins: defaultOrigins,
        shopProfile: { name: "AIソムリエ", logo: "" },
        lastSync: new Date().toISOString()
      });

      await batch.commit();
    }
  }, { useFirebaseRetry: true, maxRetries: 3 });
};

/* =========================
 * リアルタイム購読（エラーハンドリング改良）
 * ========================= */
export const subscribeToCollection = (
  path: string,
  callback: (data: DocumentData[], error?: string) => void,
  options: { retryOnError?: boolean } = {}
): Unsubscribe => {
  if (!isDbAvailable()) {
    callback([], "Firestore is not initialized.");
    return () => {};
  }
  
  const firestore = db as Firestore;
  const collRef = collection(firestore, path);
  
  let unsubscribe: Unsubscribe;
  let retryCount = 0;
  const maxRetries = 3;
  
  const setupListener = () => {
    unsubscribe = onSnapshot(
      collRef,
      (snapshot) => {
        retryCount = 0; // 成功時はリトライカウントをリセット
        const data = snapshot.docs.map((d) => ({
          ...d.data(),
          id: d.id.match(/^\d+$/) ? parseInt(d.id, 10) : d.id,
        }));
        callback(data);
      },
      (error) => {
        console.error(`Error subscribing to collection ${path}:`, error);
        
        let errorMessage = 'データの取得に失敗しました。';
        if (error.code === 'unavailable') {
          errorMessage = 'オフラインです。接続が回復すると自動的に再試行されます。';
        }
        
        callback([], errorMessage);
        
        // オフライン復帰時の自動リトライ
        if (options.retryOnError && retryCount < maxRetries && error.code === 'unavailable') {
          retryCount++;
          setTimeout(() => {
            ensureFirestoreOnline().then(() => {
              if (retryCount <= maxRetries) {
                setupListener();
              }
            });
          }, 2000 * retryCount); // 指数バックオフ
        }
      }
    );
  };
  
  setupListener();
  
  return () => {
    if (unsubscribe) unsubscribe();
  };
};

export const subscribeToDocument = (
  path: string,
  callback: (data: DocumentData | null, error?: string) => void,
  options: { retryOnError?: boolean } = {}
): Unsubscribe => {
  if (!isDbAvailable()) {
    callback(null, "Firestore is not initialized.");
    return () => {};
  }
  
  const firestore = db as Firestore;
  const docRef = doc(firestore, path);
  
  let unsubscribe: Unsubscribe;
  let retryCount = 0;
  const maxRetries = 3;
  
  const setupListener = () => {
    unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        retryCount = 0;
        callback(snapshot.exists() ? snapshot.data() : null);
      },
      (error) => {
        console.error(`Error subscribing to document ${path}:`, error);
        
        let errorMessage = 'データの取得に失敗しました。';
        if (error.code === 'unavailable') {
          errorMessage = 'オフラインです。接続が回復すると自動的に再試行されます。';
        }
        
        callback(null, errorMessage);
        
        if (options.retryOnError && retryCount < maxRetries && error.code === 'unavailable') {
          retryCount++;
          setTimeout(() => {
            ensureFirestoreOnline().then(() => {
              if (retryCount <= maxRetries) {
                setupListener();
              }
            });
          }, 2000 * retryCount);
        }
      }
    );
  };
  
  setupListener();
  
  return () => {
    if (unsubscribe) unsubscribe();
  };
};

/* =========================
 * ワイン一覧 CRUD（戻り値にServiceResult採用）
 * ========================= */
export const overwriteWineList = async (
  userId: string, 
  wines: Wine[]
): Promise<ServiceResult<void>> => {
  if (!isDbAvailable()) {
    return createResult(null, "Firestore is not initialized.");
  }

  return await withEnhancedRetry(async () => {
    const firestore = db as Firestore;
    const batch = writeBatch(firestore);
    const winesCollectionRef = collection(firestore, `users/${userId}/wines`);

    // 既存を全削除
    const existingDocs = await getDocs(winesCollectionRef);
    existingDocs.forEach((d) => batch.delete(d.ref));

    // 新規で全入替
    wines.forEach((wine) => {
      const wineDocRef = doc(winesCollectionRef, String(wine.id));
      batch.set(wineDocRef, wine);
    });

    await batch.commit();
  }, { useFirebaseRetry: true, maxRetries: 3 });
};

export const addWine = async (userId: string, wine: Wine): Promise<ServiceResult<void>> => {
  if (!isDbAvailable()) {
    return createResult(null, "Firestore is not initialized.");
  }

  return await withEnhancedRetry(async () => {
    const firestore = db as Firestore;
    
    // Firebase設定情報を取得
    const firebaseConfigEl = document.getElementById("firebase-config") as HTMLScriptElement;
    const firebaseConfig = firebaseConfigEl ? JSON.parse(firebaseConfigEl.textContent || '{}') : {};
    
    console.log('=== addWine処理開始 ===');
    console.log('📊 [DB情報] プロジェクト:', firebaseConfig.projectId || '不明');
    console.log('📊 [DB情報] データベース:', firebaseConfig.projectId + '(default)');
    console.log('👤 [ユーザー情報] UID:', userId);
    console.log('🍷 [ワイン情報] 名前:', wine.name);
    console.log('🍷 [ワイン情報] 一時ID:', wine.id);
    
    // データ正規化
    const normalizedWine = normalizeWineData(wine);
    
    // 既存のワインリストを取得してIDの重複を避ける
    const existingWinesResult = await getWineList(userId);
    const existingWineIds = existingWinesResult.success ? (existingWinesResult.data || []).map(w => w.id) : [];
    
    // 新しいIDを生成（既存の最大ID + 1）
    const newId = existingWineIds.length > 0 ? Math.max(...existingWineIds) + 1 : 1;
    
    // 正規化されたワインに新しいIDを設定
    const wineWithId = { ...normalizedWine, id: newId };
    
    const collectionPath = `users/${userId}/wines`;
    const documentId = String(newId);
    const ref = doc(firestore, collectionPath, documentId);
    
    console.log('📝 [保存先詳細] コレクション:', collectionPath);
    console.log('📝 [保存先詳細] ドキュメントID:', documentId);
    console.log('📝 [保存先詳細] フルパス:', `${collectionPath}/${documentId}`);
    console.log('📝 [保存先詳細] 生成されたID:', newId);
    
    await setDoc(ref, wineWithId);
    
    console.log('=== addWine成功 ===');
    console.log('✅ Firestoreに保存完了');
    console.log('📝 [保存完了] コレクション:', collectionPath);
    console.log('📝 [保存完了] ドキュメントID:', documentId);
    console.log('📝 [保存完了] プロジェクト:', firebaseConfig.projectId);
    console.log('🍷 [保存完了] ワイン名:', wineWithId.name);
    console.log('🍷 [保存完了] ワインID:', wineWithId.id);
    
    return { success: true, data: { id: newId }, error: null };
  }, { useFirebaseRetry: true, maxRetries: 3 });
};

export const updateWine = async (
  userId: string,
  wineId: number,
  data: Partial<Wine>
): Promise<ServiceResult<void>> => {
  if (!isDbAvailable()) {
    return createResult(null, "Firestore is not initialized.");
  }

  return await withEnhancedRetry(async () => {
    const firestore = db as Firestore;
    const ref = doc(firestore, `users/${userId}/wines`, String(wineId));
    await setDoc(ref, data, { merge: true });
  }, { useFirebaseRetry: true, maxRetries: 3 });
};

export const deleteWine = async (userId: string, wineId: number): Promise<ServiceResult<void>> => {
  if (!isDbAvailable()) {
    return createResult(null, "Firestore is not initialized.");
  }

  return await withEnhancedRetry(async () => {
    const firestore = db as Firestore;
    const ref = doc(firestore, `users/${userId}/wines`, String(wineId));
    await deleteDoc(ref);
  }, { useFirebaseRetry: true, maxRetries: 3 });
};

/* =========================
 * オプション（設定）
 * ========================= */
type OptionType = "occasions" | "tastes" | "prices" | "origins";

export const updateOptions = async (
  userId: string,
  type: OptionType,
  options: string[]
): Promise<ServiceResult<void>> => {
  if (!isDbAvailable()) {
    return createResult(null, "Firestore is not initialized.");
  }

  return await withEnhancedRetry(async () => {
    const firestore = db as Firestore;
    const ref = doc(firestore, `users/${userId}/settings`, type);
    await setDoc(ref, { options });
  }, { useFirebaseRetry: true, maxRetries: 3 });
};

/* =========================
 * ショッププロフィール
 * ========================= */
export const updateShopProfile = async (
  userId: string,
  profile: Partial<ShopProfile>
): Promise<ServiceResult<void>> => {
  if (!isDbAvailable()) {
    return createResult(null, "Firestore is not initialized.");
  }

  return await withEnhancedRetry(async () => {
    const firestore = db as Firestore;
    const ref = doc(firestore, `users/${userId}/settings`, "shopProfile");
    await setDoc(ref, profile, { merge: true });
  }, { useFirebaseRetry: true, maxRetries: 3 });
};

/* =========================
 * 読み取り専用の便利関数
 * ========================= */
export const getWineList = async (userId: string): Promise<ServiceResult<Wine[]>> => {
  if (!isDbAvailable()) {
    return createResult(null, "Firestore is not initialized.");
  }

  return await withEnhancedRetry(async () => {
    const firestore = db as Firestore;
    const winesCollectionRef = collection(firestore, `users/${userId}/wines`);
    const snapshot = await getDocs(winesCollectionRef);
    
    const wines: Wine[] = snapshot.docs.map(doc => ({
      ...doc.data(),
      id: parseInt(doc.id, 10)
    })) as Wine[];
    
    return wines;
  }, { useFirebaseRetry: true, maxRetries: 3 });
};

export const getShopProfile = async (userId: string): Promise<ServiceResult<ShopProfile>> => {
  const result = await getDocumentSafely<ShopProfile>(`users/${userId}/settings`, 'shopProfile', {
    useRetry: true,
    maxRetries: 3
  });

  return {
    data: result.data,
    error: result.error,
    success: !result.error
  };
};

/* =========================
 * データ同期機能
 * ========================= */

// 全データの同期保存
export const syncAllData = async (
  userId: string,
  data: {
    wineList: Wine[];
    occasions: string[];
    tastes: string[];
    prices: string[];
    origins: string[];
    shopProfile: ShopProfile;
  }
): Promise<ServiceResult<void>> => {
  console.log('=== syncAllData開始 ===');
  console.log('ユーザーID:', userId);
  console.log('同期するワイン数:', data.wineList.length);
  console.log('同期するワインID一覧:', data.wineList.map(w => w.id));
  
  if (!isDbAvailable()) {
    console.error('Firestore is not initialized.');
    return createResult(null, "Firestore is not initialized.");
  }

  return await withEnhancedRetry(async () => {
    console.log('=== Firestore書き込み開始 ===');
    const firestore = db as Firestore;
    const batch = writeBatch(firestore);

    // ワインリストの同期
    const winesCollectionRef = collection(firestore, `users/${userId}/wines`);
    console.log('ワインコレクション参照:', winesCollectionRef.path);
    
    // 既存のワインリストを取得して削除対象を特定
    const existingWinesResult = await getWineList(userId);
    const existingWineIds = existingWinesResult.success ? (existingWinesResult.data || []).map(w => w.id) : [];
    const newWineIds = data.wineList.map(w => w.id);
    const deletedWineIds = existingWineIds.filter(id => !newWineIds.includes(id));
    
    console.log('既存のワインID一覧:', existingWineIds);
    console.log('新しいワインID一覧:', newWineIds);
    console.log('削除対象のワインID一覧:', deletedWineIds);
    
    // 削除されたワインをFirestoreから削除
    deletedWineIds.forEach(wineId => {
      const wineRef = doc(winesCollectionRef, String(wineId));
      console.log(`ワイン${wineId}を削除:`, wineRef.path);
      batch.delete(wineRef);
    });
    
    // 新しい/更新されたワインを追加/更新（データ正規化を適用）
    data.wineList.forEach(wine => {
      const normalizedWine = normalizeWineData(wine);
      const wineRef = doc(winesCollectionRef, String(wine.id));
      console.log(`ワイン${wine.id}をバッチに追加:`, wineRef.path);
      console.log(`ワイン${wine.id}の画像データ:`, normalizedWine.image ? `あり（${normalizedWine.image.length}文字）` : 'なし');
      if (normalizedWine.image) {
        console.log(`ワイン${wine.id}の画像プレフィックス:`, normalizedWine.image.substring(0, 50) + '...');
      }
      batch.set(wineRef, normalizedWine);
    });

    // 設定の同期
    const settingsRef = doc(firestore, `users/${userId}/settings`, 'appData');
    console.log('設定ドキュメント参照:', settingsRef.path);
    
    const settingsData = {
      occasions: data.occasions,
      tastes: data.tastes,
      prices: data.prices,
      origins: data.origins,
      shopProfile: data.shopProfile,
      lastSync: new Date().toISOString()
    };
    console.log('設定データ:', settingsData);
    batch.set(settingsRef, settingsData);

    console.log('=== バッチコミット実行 ===');
    console.log('バッチオブジェクト:', batch);
    console.log('バッチの_delegate:', batch._delegate);
    
    // 安全にバッチの詳細を取得
    try {
      if (batch._delegate && batch._delegate._mutations) {
        console.log('バッチに含まれる操作数:', batch._delegate._mutations.length);
        console.log('バッチの詳細:', batch._delegate._mutations.map(m => ({
          type: m.type,
          path: m.key.path.toString(),
          data: m.data
        })));
      } else {
        console.log('バッチの詳細情報を取得できません（_delegate._mutationsが利用できません）');
      }
    } catch (detailError) {
      console.log('バッチの詳細情報取得エラー:', detailError);
    }
    
    try {
      await batch.commit();
      console.log('=== バッチコミット完了 ===');
      console.log('バッチコミットが正常に完了しました');
    } catch (commitError) {
      console.error('=== バッチコミットエラー ===');
      console.error('バッチコミットエラー:', commitError);
      console.error('エラーの型:', typeof commitError);
      console.error('エラーの詳細:', {
        code: commitError?.code || '不明',
        message: commitError?.message || '不明なエラー',
        stack: commitError?.stack || 'スタック情報なし',
        name: commitError?.name || '不明',
        toString: commitError?.toString?.() || 'toString不可'
      });
      throw commitError;
    }
  }, { useFirebaseRetry: true, maxRetries: 3 });
};

// 全データの同期取得
export const getAllSyncedData = async (userId: string): Promise<ServiceResult<{
  wineList: Wine[];
  occasions: string[];
  tastes: string[];
  prices: string[];
  origins: string[];
  shopProfile: ShopProfile;
  lastSync: string;
}>> => {
  if (!isDbAvailable()) {
    return createResult(null, "Firestore is not initialized.");
  }

  return await withEnhancedRetry(async () => {
    const firestore = db as Firestore;
    
    console.log(`🔍 [DEBUG] getAllSyncedData: ユーザーID ${userId} のデータを取得開始`);
    console.log(`🔍 [DEBUG] Firestore接続状況:`, { 
      isDbAvailable: isDbAvailable(),
      projectId: db.app.options.projectId,
      databaseId: db._delegate?._databaseId?.databaseId || 'unknown'
    });
    
    // ワインリストの取得
    const winesResult = await getWineList(userId);
    console.log(`getAllSyncedData: ワインリスト取得結果:`, winesResult);
    console.log(`getAllSyncedData: ワインリストの詳細:`, {
      success: winesResult.success,
      error: winesResult.error,
      wineCount: winesResult.data?.length || 0,
      wineIds: winesResult.data?.map(w => w.id) || []
    });
    
    // 画像データの詳細をログ出力
    if (winesResult.success && winesResult.data) {
      winesResult.data.forEach(wine => {
        console.log(`ワイン${wine.id}(${wine.name})の画像データ:`, wine.image ? `あり（${wine.image.length}文字）` : 'なし');
        if (wine.image) {
          console.log(`ワイン${wine.id}の画像プレフィックス:`, wine.image.substring(0, 50) + '...');
        }
      });
    }
    if (!winesResult.success) {
      throw new Error(winesResult.error || 'ワインリストの取得に失敗しました');
    }

    // 設定の取得
    const settingsRef = doc(firestore, `users/${userId}/settings`, 'appData');
    console.log(`getAllSyncedData: 設定ドキュメント参照:`, settingsRef.path);
    const settingsDoc = await getDoc(settingsRef);
    console.log(`getAllSyncedData: 設定ドキュメント存在確認:`, settingsDoc.exists());
    
    if (!settingsDoc.exists()) {
      console.log(`getAllSyncedData: 設定ドキュメントが存在しません。パス: ${settingsRef.path}`);
      throw new Error('同期データが見つかりません');
    }

    const settingsData = settingsDoc.data();
    console.log(`getAllSyncedData: 設定データ:`, settingsData);
    
    const result = {
      wineList: winesResult.data || [],
      occasions: settingsData.occasions || [],
      tastes: settingsData.tastes || [],
      prices: settingsData.prices || [],
      origins: settingsData.origins || [],
      shopProfile: settingsData.shopProfile || {},
      lastSync: settingsData.lastSync || new Date().toISOString()
    };
    
    console.log(`getAllSyncedData: 最終結果:`, result);
    return result;
  }, { useFirebaseRetry: true, maxRetries: 3 });
};

// リアルタイム同期リスナーの設定（現在無効化）
export const setupRealtimeSync = (
  userId: string,
  onDataChange: (data: {
    wineList: Wine[];
    occasions: string[];
    tastes: string[];
    prices: string[];
    origins: string[];
    shopProfile: ShopProfile;
    lastSync: string;
  }) => void
): Unsubscribe => {
  console.log('リアルタイム同期は現在無効化されています');
  // 何もしない関数を返す
  return () => {};
};

// 静的HTMLを保存する関数（Firebase Storage版 - サイズ制限なし）
export const saveStaticWineList = async (userId: string, data: AppData): Promise<ServiceResult<{ html: string; url: string; timestamp: number }>> => {
  if (!isDbAvailable()) {
    return createResult(null, "Firestore is not initialized.");
  }

  try {
    console.log('=== 静的HTML生成開始（Storage版） ===');
    console.log('userId:', userId);
    console.log('ワイン数:', data.wineList.length);
    
    // Firebase Storageにワインデータを分割アップロード
    const { uploadWineDataToStorage } = await import('../utils/wineStorageService');
    const { manifestUrl, htmlUrl } = await uploadWineDataToStorage(userId, data);
    
    console.log('=== 静的HTML保存完了（Storage版） ===');
    console.log('✅ マニフェストURL:', manifestUrl);
    console.log('✅ HTML URL:', htmlUrl);
    
    return createResult({
      html: '', // HTMLはStorageに保存されているため空
      url: htmlUrl,
      timestamp: Date.now()
    });
  } catch (error) {
    console.error('静的HTML保存エラー:', error);
    return createResult(null, `静的HTML保存失敗: ${error}`);
  }
};

// 静的HTMLを取得する関数
export const getStaticWineList = async (userId: string): Promise<ServiceResult<StaticHtmlData>> => {
  if (!isDbAvailable()) {
    return createResult(null, "Firestore is not initialized.");
  }

  try {
    const firestore = db as Firestore;
    const staticHtmlRef = doc(firestore, 'static-wine-lists', userId);
    const docSnap = await getDoc(staticHtmlRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return createResult({
        html: data.html,
        url: data.url,
        timestamp: data.timestamp
      });
    } else {
      return createResult(null, "静的HTMLが見つかりません");
    }
  } catch (error) {
    console.error('静的HTML取得エラー:', error);
    return createResult(null, `静的HTML取得失敗: ${error}`);
  }
};

// 簡単な同期テスト関数
export const testSync = async (userId: string): Promise<ServiceResult<string>> => {
  if (!isDbAvailable()) {
    return createResult(null, "Firestore is not initialized.");
  }

  try {
    const firestore = db as Firestore;
    const testRef = doc(firestore, `users/${userId}/test`, 'connection');
    
    // 簡単なテストデータを書き込み
    await setDoc(testRef, {
      timestamp: new Date().toISOString(),
      message: 'Connection test successful'
    });
    
    console.log('Firebase接続テスト成功');
    return createResult('接続テスト成功');
  } catch (error: any) {
    console.error('Firebase接続テスト失敗:', error);
    return createResult(null, `接続テスト失敗: ${error.message}`);
  }
};